import * as S from "./styles.ts";
import { useState } from "react";
import { ColorKey } from "@template/theme/types/color.type.ts";
import { InputLabel } from "@template/components/Input/styles.ts";
import Popover from "../Popover/index.tsx";
import { CircleHelp } from "lucide-react";

export interface ISwitchProps {
  checked?: boolean;
  onChange?: (checked: boolean, e?: any) => void;
  disabled?: boolean;
  label?: string;
  name?: string;
  wrapperProps?: any;
  color?: ColorKey;
  variant?: "flat" | "light";
  tooltip?: string;
}

const Help = ({ tip }: { tip: string }) => {
  if (!tip) return null;

  const handleTriggerClick = (e: React.MouseEvent) => {
    // This prevents the event from "bubbling" up to the parent toggle/switch
    e.stopPropagation();
  };

  return (
    <Popover
      tip={tip}
      delay={100}
    >
      <S.Help
        as="button"
        type="button"
        aria-label="Help information"
        onClick={handleTriggerClick}
        onMouseDown={handleTriggerClick}
      >
        <CircleHelp aria-hidden="true" />
      </S.Help>
    </Popover>
  );
};

const Switch = ({
  checked,
  onChange,
  disabled,
  label,
  name,
  wrapperProps,
  color = "primary",
  variant = "light",
  tooltip,
}: ISwitchProps) => {
  const [defaultChecked, setDefaultChecked] = useState(false);
  const usedChecked = typeof checked === "boolean" ? checked : defaultChecked;
  const usedOnChange = onChange !== undefined ? onChange : setDefaultChecked;

  const handleChange = (e: any) => {
    if (disabled) return;
    usedOnChange(!usedChecked, e);
  };

  return (
    <S.Wrapper
      onClick={handleChange}
      $variant={variant}
      {...wrapperProps}
      style={{
        cursor: disabled ? "default" : "pointer",
        ...(wrapperProps?.style ? wrapperProps.style : {}),
      }}
    >
      {label && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
          <InputLabel
            style={{
              cursor: disabled ? "default" : "pointer",
            }}
          >
            {label}
          </InputLabel>
          {tooltip && <Help tip={tooltip} />}
        </div>
      )}
      <S.SwitchContainer>
        <S.GhostInput
          type="checkbox"
          checked={usedChecked}
          name={name || label}
          disabled={disabled}
          onChange={handleChange}
        />
        <S.InputStyle
          $checked={usedChecked}
          $disabled={disabled}
          $color={color}
        />
      </S.SwitchContainer>
    </S.Wrapper>
  );
};

export default Switch;
